import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { BTSport } from "../../models/bt-sport";
import { BTSportService } from "../../services/bt-sport.service";
import { ProgramArticleService } from "../../services/program-article.service";
import { SidenavService } from "../../services/sidenav.service";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { ReferentialEntity } from "../../models/referential-entity";
import { ReferentialService } from "../../services/referential.service";
import { BTSportPossibleDatetime } from "../../models/bt-sport-possible-datetime";

@Component({
  selector: "app-bt-sport",
  templateUrl: "./bt-sport.component.html",
  styleUrls: ["./bt-sport.component.scss"],
})
export class BTSportComponent implements OnInit, OnDestroy {
  loading: boolean;

  // Filters
  termTypes: any[];
  termTypeSelected: any;
  term: string;

  startDate: Date;
  endDate: Date;

  sportsSearch: any[];
  sportsSelected: any[];

  channels: any[];
  channelsSelected: any[];

  statusAvailable: any[];
  statusSelected: any;

  masterSpevContribued: boolean;
  spevContribued: boolean;

  showReplay: boolean;

  // Datas
  btSportProducts: BTSport[];
  filteredBtSportProducts: BTSport[];
  btSport: BTSport;
  possibleDatetime: BTSportPossibleDatetime;

  // Filters details
  sportTerm: string;
  sports: ReferentialEntity[];
  filteredSports: ReferentialEntity[];

  ageMarks: any[];

  manualSportingEventId: number;
  manualSportingEventError: boolean;

  masterSportingEventTerm: string;
  masterSportingEvents: any[];
  filteredMasterSportingEvents: any[];

  sportingEventTerm: string;
  sportingEvents: any[];
  filteredSportingEvents: any[];

  typeTerm: string;
  types: any[];
  filteredTypes: any[];

  groupTerm: string;
  groups: any[];
  filteredGroups: any[];

  labelTerm: string;
  labels: any[];
  filteredLabels: any[];

  // Details
  updating: boolean;
  copyTooltip: string;

  liveStartDate: Date;
  liveEndDate: Date;
  liveTimingChange: boolean;

  constructor(
    private router: Router,
    public referentialService: ReferentialService,
    private activatedRoute: ActivatedRoute,
    private title: Title,
    private btSportService: BTSportService,
    private programArticleService: ProgramArticleService,
    private sidenavService: SidenavService
  ) {
    this.title.setTitle("Taiga - TNT Sports");
    this.sidenavService.currentTab = "TNT Sports";

    // Filters
    this.termTypes = ["All", "Product code", "Title", "Description"];
    this.termTypeSelected = this.termTypes[0];
    this.term = "";
    this.startDate = new Date();
    this.endDate = new Date();
    this.endDate.setDate(this.endDate.getDate() + 7);
    this.masterSpevContribued = null;
    this.spevContribued = null;
    this.possibleDatetime = null;
    this.manualSportingEventError = false;

    this.statusAvailable = [
      {
        value: "All",
        masterSportingEventContribued: null,
        sportingEventContribued: null,
      },
      {
        value: "Contribued",
        masterSportingEventContribued: true,
        sportingEventContribued: true,
      },
      {
        value: "TBA",
        masterSportingEventContribued: true,
        sportingEventContribued: false,
      },
      {
        value: "Not contribued",
        masterSportingEventContribued: false,
        sportingEventContribued: false,
      },
    ];
    this.statusSelected = this.statusAvailable[0].value;
    this.showReplay = false;

    // Details
    this.copyTooltip = "Copy";
    this.updating = false;
  }

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading = true;

    // Get all ageMarks
    this.btSportService.GetAgeMarks().subscribe((ageMarks) => {
      this.ageMarks = ageMarks;

      this.btSportService
        .GetAllProducts()
        .subscribe((btSportProducts: BTSport[]) => {
          // Get agemark name
          btSportProducts.forEach((element) => {
            this.getAgeMarkName(element);
          });
          this.btSportProducts = btSportProducts;

          this.sportsSearch = this.btSportProducts
            .map((bt) => bt.sportName)
            .filter((item, i, ar) => ar.indexOf(item) === i)
            .sort((a, b) => a.localeCompare(b));

          this.sportsSelected = this.sportsSearch;

          this.channels = this.btSportProducts
            .map((bt) => bt.channel)
            .filter((item, i, ar) => ar.indexOf(item) === i)
            .sort((a, b) => a.localeCompare(b));

          this.channelsSelected = this.channels;

          this.activatedRoute.params.subscribe((params: Params) => {
            if (params.term) {
              this.term = params.term;

              let product = this.btSportProducts.filter((btSportProduct) => {
                return (
                  btSportProduct.btSportProductCode
                    .toLowerCase()
                    .indexOf(this.term.toLowerCase()) >= 0
                );
              })[0];

              this.showReplay = product.broadcastName !== "Live";
              this.startDate = product.liveDate;
              this.endDate = product.liveEnd;
            }
            this.setFilteredBtSportProducts();
          });
        });
    });
  }

  getAgeMarkName(btSport: BTSport) {
    let ageMark = this.ageMarks.find(
      (am) => am.ageMarkTemplateId === btSport.ageMarkId
    );

    if (ageMark) {
      btSport.ageMarkName = ageMark.name;
    } else {
      btSport.ageMarkName = "";
    }
  }

  /**
   * Set the contribued value and filter the sportingEvents
   * @param masterValue : is Master SPEV contribued value
   * @param spevValue : is SPEV contribued value
   */
  setContribued(masterValue: boolean, spevValue: boolean) {
    this.masterSpevContribued = masterValue;
    this.spevContribued = spevValue;
    this.setFilteredBtSportProducts();
  }

  setShowReplay(event: any) {
    this.showReplay = event.checked;
    this.setFilteredBtSportProducts();
  }

  /**
   * Set the list to show by filters
   */
  setFilteredBtSportProducts() {
    // Initialize
    this.filteredBtSportProducts = this.btSportProducts;

    // Set StartDate Hours to 00:00:00 for filtering
    let startDate = new Date(this.startDate);
    startDate.setHours(0);
    startDate.setMinutes(0);
    startDate.setSeconds(0);

    // increase one day to get all day
    let endDate = new Date(this.endDate);
    endDate.setHours(23);
    endDate.setMinutes(59);
    endDate.setSeconds(59);

    // Filter by dates
    this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
      (btSportProduct) => {
        return (
          btSportProduct.liveDate.getTime() >= startDate.getTime() &&
          btSportProduct.liveDate.getTime() <= endDate.getTime()
        );
      }
    );

    // Filter out or not replays
    if (!this.showReplay) {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return btSportProduct.broadcastName === "Live";
        }
      );
    }

    // Filter by term, depending of term type
    if (this.termTypeSelected === "Product code") {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            btSportProduct.btSportProductCode
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0
          );
        }
      );
    } else if (this.termTypeSelected === "Title") {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            btSportProduct.name
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0
          );
        }
      );
    } else if (this.termTypeSelected === "Description") {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            btSportProduct.description
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0
          );
        }
      );
    } else if (this.termTypeSelected === "All") {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            btSportProduct.btSportProductCode
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0 ||
            btSportProduct.name
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0 ||
            btSportProduct.description
              .toLowerCase()
              .indexOf(this.term.toLowerCase()) >= 0
          );
        }
      );
    }

    // Filter by sports
    this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
      (btSportProduct) => {
        return this.sportsSelected.includes(btSportProduct.sportName);
      }
    );

    // Filter by channels
    this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
      (btSportProduct) => {
        return this.channelsSelected.includes(btSportProduct.channel);
      }
    );

    // Filter by status
    // Contribued
    if (this.masterSpevContribued === true && this.spevContribued === true) {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            (!btSportProduct.isSportingEvent &&
              btSportProduct.labelId &&
              btSportProduct.labelId >= 0) ||
            (btSportProduct.masterSportingEventId &&
              btSportProduct.masterSportingEventId != 0 &&
              btSportProduct.isSportingEvent &&
              btSportProduct.sportingEventId &&
              btSportProduct.sportingEventId != 0)
          );
        }
      );
    }

    // TBA
    else if (
      this.masterSpevContribued === true &&
      this.spevContribued === false
    ) {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            btSportProduct.isSportingEvent &&
            btSportProduct.masterSportingEventId &&
            btSportProduct.masterSportingEventId != 0 &&
            btSportProduct.sportingEventId &&
            btSportProduct.sportingEventId === 0
          );
        }
      );
    }

    // Not contribued
    else if (
      this.masterSpevContribued === false &&
      this.spevContribued === false
    ) {
      this.filteredBtSportProducts = this.filteredBtSportProducts.filter(
        (btSportProduct) => {
          return (
            (!btSportProduct.isSportingEvent &&
              btSportProduct.labelId &&
              btSportProduct.labelId < 0) ||
            (btSportProduct.isSportingEvent &&
              btSportProduct.masterSportingEventId &&
              btSportProduct.masterSportingEventId === 0)
          );
        }
      );
    }

    if (this.filteredBtSportProducts.length > 0) {
      this.setBTSportProductToEdit(this.filteredBtSportProducts[0]);
    } else {
      this.btSport = null;
    }
    this.loading = false;
  }

  selectAllSports(event: any) {
    if (event.checked) {
      this.sportsSelected = this.sportsSearch;
    } else {
      this.sportsSelected = [];
    }
    this.setFilteredBtSportProducts();
  }

  allSportsSelected() {
    return this.sportsSelected.length === this.sportsSearch.length;
  }

  selectAllChannels(event: any) {
    if (event.checked) {
      this.channelsSelected = this.channels;
    } else {
      this.channelsSelected = [];
    }
    this.setFilteredBtSportProducts();
  }

  allChannelsSelected() {
    return this.channelsSelected.length === this.channels.length;
  }

  /**
   * Define the current tnt sports as active in the list
   * @param {BTSport} btSport
   */
  setBTSportProductToEdit(btSport: BTSport) {
    this.btSportProducts.map((item) => (item.active = false));
    btSport.active = true;
    this.btSport = btSport;
    this.liveTimingChange = false;
    this.liveStartDate = btSport.liveDate;
    this.liveEndDate = btSport.liveEnd;

    this.manualSportingEventId = null;
    this.sports = [];
    this.filteredSports = [];

    this.masterSportingEvents = null;
    this.filteredSportingEvents = null;

    this.sportingEvents = null;
    this.filteredSportingEvents = null;

    if (this.btSport.broadcastName === "Live") {
      this.getLiveDateTime();
    }

    this.loadSports();
    this.loadMasterSportingEvents();
    this.loadTypes();
  }

  ExtractExcel() {
    this.exportAsExcelFile(this.filteredBtSportProducts, "BT_SPORT");
  }

  exportAsExcelFile(json: any[], excelFileName: string): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ["data"],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getDate() + ".xlsx"
    );
  }

  /**
   * Clear the filters
   */
  searchClear() {
    this.term = "";
    this.startDate = new Date();
    this.endDate = new Date();
    this.endDate.setDate(this.endDate.getDate() + 7);
    this.termTypeSelected = this.termTypes[0];
    this.sportsSelected = this.sportsSearch;
    this.channelsSelected = this.channels;
    this.statusSelected = this.statusAvailable[0].value;
    this.setContribued(null, null);
  }

  // Load sports
  loadSports() {
    this.referentialService
      .searchReferential("0", 0, false, [])
      .subscribe((result: any[]) => {
        this.sports = result.filter((r) => !r.lockStatus);
        this.sportTerm = "";
        this.searchSport();
      });
  }

  searchSport() {
    this.filteredSports = this.sports.filter((str) => {
      return str.name.toLowerCase().indexOf(this.sportTerm.toLowerCase()) >= 0;
    });
  }

  changeSport(event) {
    this.updating = true;
    // Update Sport, then reset master SPEV / SPEV
    this.btSport.chapcaSportId = event.value;

    // Change sport name
    this.btSport.sportName = this.sports.find(
      (sport) => sport.id === event.value
    ).name;

    this.btSportService
      .UpdateBTSportSport(
        this.btSport.btSportProductCode,
        this.btSport.chapcaSportId,
        this.btSport.ageMarkId
      )
      .subscribe(() => {
        this.btSport.masterSportingEventId = null;
        this.btSport.sportingEventId = null;
        this.btSportService
          .UpdateBTSportProduct(
            this.btSport.btSportProductCode,
            null,
            null,
            this.btSport.liveDate,
            this.btSport.liveEnd
          )
          .subscribe(() => {
            this.loadMasterSportingEvents();
            this.updating = false;
          });
      });
  }

  changeAgeMark(event) {
    this.updating = true;
    this.btSport.ageMarkId = event.value;

    this.btSportService
      .UpdateBTSportSport(
        this.btSport.btSportProductCode,
        this.btSport.chapcaSportId,
        this.btSport.ageMarkId
      )
      .subscribe(() => {
        this.getAgeMarkName(this.btSport);
        this.updating = false;
      });
  }

  unlinkAgeMark() {
    this.updating = true;
    this.btSport.ageMarkId = -1;

    this.btSportService
      .UpdateBTSportSport(
        this.btSport.btSportProductCode,
        this.btSport.chapcaSportId,
        this.btSport.ageMarkId
      )
      .subscribe(() => {
        this.getAgeMarkName(this.btSport);
        this.updating = false;
      });
  }

  // Is Live Datetime available
  getLiveDateTime() {
    this.btSportService
      .GetPossibleLiveDatetime(this.btSport.btSportProductCode)
      .subscribe((result: BTSportPossibleDatetime) => {
        if (
          this.liveStartDate.getTime() !== result.possibleStartTime.getTime() ||
          this.liveEndDate.getTime() !== result.possibleEndTime.getTime()
        ) {
          this.possibleDatetime = result;
        } else {
          this.possibleDatetime = null;
        }
      });
  }

  updateLiveTimings() {
    this.liveTimingChange = true;
    this.liveStartDate = this.possibleDatetime.possibleStartTime;
    this.liveEndDate = this.possibleDatetime.possibleEndTime;
    this.possibleDatetime = null;
  }

  changeLiveDate(event) {
    // If new live date is superior to current live end, change live end
    if (event.value.getTime() > this.btSport.liveEnd.getTime()) {
      this.liveEndDate = event.value;
    } else {
      this.liveStartDate = event.value;
    }

    this.liveTimingChange = true;
  }

  changeLiveEnd(event) {
    // If new live end is inferior to current live date, change live date
    if (event.value.getTime() < this.btSport.liveDate.getTime()) {
      this.liveStartDate = event.value;
    } else {
      this.liveEndDate = event.value;
    }
    this.liveTimingChange = true;
  }

  saveLiveTiming() {
    this.updating = true;
    this.btSportService
      .UpdateBTSportProduct(
        this.btSport.btSportProductCode,
        this.btSport.masterSportingEventId,
        this.btSport.sportingEventId,
        this.liveStartDate,
        this.liveEndDate
      )
      .subscribe(() => {
        this.btSport.liveDate = this.liveStartDate;
        this.btSport.liveEnd = this.liveEndDate;
        this.updating = false;
        this.liveTimingChange = false;
      });
  }

  refreshDataAfterTimingUpdate() {
    this.getLiveDateTime();
    this.btSport.masterSportingEventId = null;
    this.btSport.sportingEventId = null;
    this.masterSportingEvents = null;
    this.sportingEvents = null;
    this.updating = true;
    // When updating the live date, refresh the list of master sporting event
    // update when master spev is not contribued
    if (!this.btSport.masterSportingEventId) {
      this.btSportService
        .GetMasterSportingEvents(this.btSport.chapcaSportId, this.liveStartDate)
        .subscribe((result) => {
          this.masterSportingEvents = result.parents.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          this.masterSportingEventTerm = "";
          this.searchMasterSportingEvent();
          this.updating = false;
        });
    }
  }

  // Load master sporting events
  loadMasterSportingEvents() {
    this.btSportService
      .GetMasterSportingEvents(this.btSport.chapcaSportId, this.liveStartDate)
      .subscribe((result) => {
        this.masterSportingEvents = result.parents.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        this.masterSportingEventTerm = "";
        this.searchMasterSportingEvent();

        // If product has master sporting event but does not exists in list,
        // create list with one entry
        // This can happens when users force a Sporting Event

        if (
          this.btSport.masterSportingEventId &&
          this.btSport.masterSportingEventId > 0 &&
          !this.masterSportingEvents.find(
            (se) => se.id === this.btSport.masterSportingEventId
          )
        ) {
          this.programArticleService
            .GetSportingEvent(this.btSport.masterSportingEventId)
            .subscribe((res) => {
              this.btSport.masterSportingEventName = res.name;

              let masterSportingEvent = {
                id: this.btSport.masterSportingEventId,
                name: res.name,
              };

              this.masterSportingEvents = [masterSportingEvent];

              if (!this.masterSportingEventTerm) {
                this.masterSportingEventTerm = "";
              }

              this.searchMasterSportingEvent();

              // Then, do the same for sporting event Id
              if (
                this.btSport.sportingEventId &&
                this.btSport.sportingEventId > 0
              ) {
                this.programArticleService
                  .GetSportingEvent(this.btSport.sportingEventId)
                  .subscribe((res) => {
                    let sportingEvent = {
                      sportingEventId: res.sportingEventId,
                      name: res.name,
                    };

                    this.sportingEvents = [sportingEvent];

                    if (!this.sportingEventTerm) {
                      this.sportingEventTerm = "";
                    }

                    this.searchSportingEvent();
                  });
              }
            });
        } else if (
          this.btSport.masterSportingEventId &&
          this.btSport.masterSportingEventId > 0
        ) {
          // then get sporting events if Master sporting event has been selected
          this.loadSportingEvents();
        }
      });
  }

  loadTypes() {
    this.referentialService
      .searchReferential("Type", 2, true, [])
      .subscribe((result) => {
        this.types = result;
        this.typeTerm = "";
        this.searchType();

        if (this.btSport.typeId && this.btSport.typeId !== -1) {
          this.loadGroup(this.btSport.typeId);
        }
      });
  }

  changeType(event) {
    this.btSport.groupId = -1;
    this.btSport.labelId = -1;
    this.loadGroup(event.value);
  }

  loadGroup(typeId: number) {
    let type = this.types.find((t) => t.id === typeId);
    type.referentialId = "Type";
    this.referentialService
      .searchReferential("Group", 2, true, [type])
      .subscribe((result) => {
        this.groups = result;
        this.groupTerm = "";
        this.searchGroup();

        if (this.btSport.groupId && this.btSport.groupId != -1) {
          this.loadLabel(this.btSport.groupId);
        }
      });
  }

  changeGroup(event) {
    this.btSport.labelId = -1;
    this.loadLabel(event.value);
  }

  loadLabel(groupId: number) {
    let group = this.groups.find((t) => t.id === groupId);
    group.referentialId = "Group";
    this.referentialService
      .searchReferential("Label", 2, true, [group])
      .subscribe((result) => {
        this.labels = result;
        this.labelTerm = "";
        this.searchLabel();
      });
  }

  changeLabel(event) {
    this.updating = true;
    this.btSportService
      .UpdateBTSportNonSportiveClassification(
        this.btSport.btSportProductCode,
        this.btSport.typeId,
        this.btSport.groupId,
        event.value
      )
      .subscribe(() => {
        this.updating = false;
      });
  }

  resetNonSport() {
    this.btSport.typeId = -1;
    this.btSport.groupId = -1;
    this.btSport.labelId = -1;
    this.changeLabel({ value: -1 });
  }

  searchType() {
    this.filteredTypes = this.types.filter((str) => {
      return str.name.toLowerCase().indexOf(this.typeTerm.toLowerCase()) >= 0;
    });
  }

  searchGroup() {
    this.filteredGroups = this.groups.filter((str) => {
      return str.name.toLowerCase().indexOf(this.groupTerm.toLowerCase()) >= 0;
    });
  }

  searchLabel() {
    this.filteredLabels = this.labels.filter((str) => {
      return str.name.toLowerCase().indexOf(this.labelTerm.toLowerCase()) >= 0;
    });
  }

  searchMasterSportingEvent() {
    this.filteredMasterSportingEvents = this.masterSportingEvents.filter(
      (str) => {
        return (
          str.name
            .toLowerCase()
            .indexOf(this.masterSportingEventTerm.toLowerCase()) >= 0
        );
      }
    );
  }

  changeMasterSportingEvent(event) {
    this.updating = true;
    this.btSport.masterSportingEventId = event.value;
    this.btSport.sportingEventId = null;
    this.btSportService
      .UpdateBTSportProduct(
        this.btSport.btSportProductCode,
        event.value,
        null,
        this.btSport.liveDate,
        this.btSport.liveEnd
      )
      .subscribe(() => {
        // Specific case: when the sporting event id was forced, the list of master sporting can be misguiding
        // if the list of master sporting only contains the master sporting event and event.value is null
        // Reload the list properly
        if (this.masterSportingEvents.length === 1 && !event.value) {
          this.loadMasterSportingEvents();
        }
        this.loadSportingEvents();

        if (this.masterSportingEvents.find((se) => se.id === event.value)) {
          this.btSport.masterSportingEventName = this.masterSportingEvents.find(
            (se) => se.id === event.value
          ).name;
        }
        this.updating = false;
      });
  }

  loadSportingEvents() {
    this.btSportService
      .GetSportingEvents(this.btSport.masterSportingEventId, this.liveStartDate)
      .subscribe((result) => {
        this.sportingEvents = result.result.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        this.sportingEventTerm = "";
        this.searchSportingEvent();
      });
  }

  searchSportingEvent() {
    this.filteredSportingEvents = this.sportingEvents.filter((str) => {
      return (
        str.name.toLowerCase().indexOf(this.sportingEventTerm.toLowerCase()) >=
          0 ||
        str.sportingEventId.toString().indexOf(this.sportingEventTerm) >= 0
      );
    });
  }

  changeSportingEvent(event) {
    this.updating = true;
    this.btSport.sportingEventId = event.value;
    this.btSportService
      .UpdateBTSportProduct(
        this.btSport.btSportProductCode,
        this.btSport.masterSportingEventId,
        event.value,
        this.liveStartDate,
        this.liveEndDate
      )
      .subscribe(() => {
        if (event.value && this.sportingEvents) {
          this.btSport.sportingEventName = this.sportingEvents.find(
            (se) => se.sportingEventId === event.value
          ).name;
        } else {
          this.btSport.sportingEventName = "";
        }
        this.updating = false;
      });
  }

  changeIsSportingEvent(event) {
    this.updating = true;
    this.btSportService
      .UpdateBTSportProductType(this.btSport.btSportProductCode, event.checked)
      .subscribe(() => {
        this.btSport.isSportingEvent = event.checked;
        if (!event.checked) {
          this.btSport.sportingEventId = null;
        } else {
          this.btSport.typeId = -1;
          this.btSport.groupId = -1;
          this.btSport.labelId = -1;
        }
        this.updating = false;
      });
  }

  copyDescription() {
    window.navigator["clipboard"].writeText(this.btSport.description);
    this.copyTooltip = "Copied!";
    setTimeout(() => (this.copyTooltip = "Copy"), 2000);
  }

  startDateChanged() {
    if (this.startDate) {
      console.log(this.startDate);
      if (this.startDate.getTime() > this.endDate.getTime()) {
        this.endDate = this.startDate;
      }
      this.setFilteredBtSportProducts();
      console.log(this.startDate);
    }
  }

  endDateChanged() {
    if (this.endDate) {
      if (this.endDate.getTime() < this.startDate.getTime()) {
        this.startDate = this.endDate;
      }

      this.setFilteredBtSportProducts();
    }
  }

  /**
   * Open the modal that shows all the emissions
   */
  seeEmissions() {
    this.router.navigate([
      {
        outlets: {
          modal: [
            "view",
            "replays",
            0,
            0,
            2,
            "Digital",
            this.btSport.btSportProductCode,
            0,
          ],
        },
      },
    ]);
  }

  applyManualSportingEventId() {
    this.updating = true;
    this.manualSportingEventError = false;

    // Check if Sporting Event Exists
    this.programArticleService
      .GetSportingEvent(this.manualSportingEventId)
      .subscribe((res) => {
        if (res.name) {
          this.btSport.sportingEventId = res.sportingEventId;
          this.btSport.sportingEventName = res.name;

          let sportElement = res.elements.find((e) => e.name === "SPORT");
          this.btSport.chapcaSportId = sportElement.idElement;
          this.btSport.sportName = this.sports.find(
            (sport) => sport.id === this.btSport.chapcaSportId
          ).name;

          let sportingEvent = {
            sportingEventId: res.sportingEventId,
            name: res.name,
          };

          this.sportingEvents = [sportingEvent];

          if (!this.sportingEventTerm) {
            this.sportingEventTerm = "";
          }

          this.searchSportingEvent();
          this.programArticleService
            .GetMasterSportingEventId(this.manualSportingEventId)
            .subscribe((res) => {
              this.btSport.masterSportingEventId = res;
              this.programArticleService
                .GetSportingEvent(res)
                .subscribe((res) => {
                  this.btSport.masterSportingEventName = res.name;

                  let masterSportingEvent = {
                    id: this.btSport.masterSportingEventId,
                    name: res.name,
                  };

                  this.masterSportingEvents = [masterSportingEvent];

                  if (!this.masterSportingEventTerm) {
                    this.masterSportingEventTerm = "";
                  }

                  this.searchMasterSportingEvent();

                  // Update for real
                  this.btSportService
                    .UpdateBTSportSport(
                      this.btSport.btSportProductCode,
                      this.btSport.chapcaSportId,
                      this.btSport.ageMarkId
                    )
                    .subscribe(() => {
                      this.btSportService
                        .UpdateBTSportProduct(
                          this.btSport.btSportProductCode,
                          this.btSport.masterSportingEventId,
                          this.btSport.sportingEventId,
                          this.liveStartDate,
                          this.liveEndDate
                        )
                        .subscribe(() => {
                          this.updating = false;
                        });
                    });
                });
            });
        } else {
          // Error message
          this.manualSportingEventError = true;
          this.updating = false;
        }
      });
  }

  ngOnDestroy() {}
}



// WEBPACK FOOTER //
// ./src/app/pages/bt-sport/bt-sport.component.ts